// src/pages/AuthPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import appIcon from '../assets/app-icon.png';
import useAuthFlow from '../hooks/useAuthFlow';
import { COUNTRY_OPTIONS, LANGUAGE_OPTIONS } from '../constants/profileOptions';
import pendingUrlService from '../services/pendingUrlService';
import analyticsService from '../services/analyticsService';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: 'wand-magic-sparkles', title: 'Upscale ×2', desc: 'Passe de 720p à 1440p en qualité pro' },
  { icon: 'bolt', title: 'Traitement rapide', desc: 'Résultats en quelques minutes grâce au cloud' },
  { icon: 'download', title: 'Sans watermark', desc: 'Télécharge tes créations sans filigrane' },
  { icon: 'shield-halved', title: 'Données sécurisées', desc: 'Tes fichiers sont chiffrés et privés' },
];

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#EA4335" d="M12 5.04c1.84 0 3.36.8 4.36 1.48l3.24-3.16C17.52 1.52 15.04.48 12 .48 7.32.48 3.28 3.24 1.28 7.2l3.76 2.92C6 7.04 8.72 5.04 12 5.04z"/>
    <path fill="#4285F4" d="M23.52 12.28c0-.92-.08-1.8-.24-2.64H12v5h6.48c-.28 1.52-1.12 2.8-2.4 3.68l3.68 2.84c2.16-2 3.4-4.92 3.4-8.44z"/>
    <path fill="#FBBC05" d="M5.04 14.28c-.24-.72-.36-1.48-.36-2.28s.12-1.56.36-2.28L1.28 7.2C.48 8.8 0 10.36 0 12s.48 3.2 1.28 4.8l3.76-2.52z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.96-2.92l-3.68-2.84c-1.08.72-2.44 1.16-4.28 1.16-3.28 0-6-2-7.04-4.8l-3.76 2.92C3.28 20.76 7.32 24 12 24z"/>
  </svg>
);

const FeatureCard = ({ icon, title, desc }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 transition-all hover:bg-white/10 hover:border-white/20">
    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
      <FontAwesomeIcon icon={icon} className="text-white text-sm" />
    </div>
    <div>
      <h3 className="font-semibold text-white text-sm">{title}</h3>
      <p className="text-xs text-slate-300 mt-0.5">{desc}</p>
    </div>
  </div>
);

const InputField = ({ id, label, type = 'text', value, onChange, placeholder, required = false, autoComplete, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
      {label}
    </label>
    {children || (
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
      />
    )}
  </div>
);

const SelectField = ({ id, label, value, onChange, options, required = false }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
    >
      {options.map((opt) => (
        <option key={opt.code} value={opt.code}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const PasswordField = ({ id, label, value, onChange, placeholder, showPassword, onToggle, autoComplete }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        autoComplete={autoComplete}
        className="w-full px-4 py-2.5 pr-12 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        aria-label={showPassword ? 'Masquer' : 'Afficher'}
      >
        <FontAwesomeIcon icon={showPassword ? 'eye-slash' : 'eye'} className="text-sm" />
      </button>
    </div>
  </div>
);

const Divider = ({ text }) => (
  <div className="flex items-center gap-3 my-5">
    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
    <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">{text}</span>
    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
  </div>
);

const Alert = ({ type, message, onDismiss }) => {
  if (!message) return null;
  const styles = {
    error: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-500/50 text-red-700 dark:text-red-300',
    success: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-500/50 text-emerald-700 dark:text-emerald-300',
    info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-500/50 text-blue-700 dark:text-blue-300',
  };
  
  return (
    <div className={`px-4 py-3 rounded-lg border text-sm flex items-start gap-3 ${styles[type]}`}>
      <FontAwesomeIcon icon={type === 'error' ? 'circle-exclamation' : type === 'success' ? 'circle-check' : 'circle-info'} className="mt-0.5 flex-shrink-0" />
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity">
          <FontAwesomeIcon icon="times" />
        </button>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get redirect URL from query params (e.g., ?next=/upscale)
  const redirectUrl = useMemo(() => {
    const next = searchParams.get('next');
    // Validate that it's a safe internal path
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      return next;
    }
    return '/dashboard';
  }, [searchParams]);

  // Check if there's a pending Sora URL
  const hasPendingUrl = useMemo(() => pendingUrlService.hasPendingUrl(), []);
  
  // Détermine le mode initial basé sur l'URL
  const getInitialMode = () => {
    const path = location.pathname;
    if (path === '/signup' || path === '/register') return 'signup';
    if (path === '/reset-password') return 'reset';
    return 'login';
  };

  // États du formulaire
  const [mode, setMode] = useState(getInitialMode);
  const [step, setStep] = useState(1); // 1 = auth, 2 = compléter profil après Google
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // États du profil
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('FR');
  const [language, setLanguage] = useState('fr');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  // Google pending - utilisateur a choisi son compte, doit compléter son profil
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  // Handler for successful authentication with tracking
  const handleAuthSuccess = (isSignup = false, method = 'email') => {
    if (isSignup) {
      analyticsService.trackSignupCompleted({ method, hasPendingUrl });
    } else {
      analyticsService.trackLoginCompleted({ method, hasPendingUrl });
    }
    navigate(redirectUrl);
  };

  const {
    loading,
    errorMsg,
    infoMsg,
    googleOnly,
    setErrorMsg,
    clearMessages,
    loginWithEmail,
    registerWithEmail,
    resetPasswordFlow,
    googleAuth,
  } = useAuthFlow();

  // Reset step when mode changes
  useEffect(() => {
    setStep(1);
    setPendingGoogleUser(null);
  }, [mode]);

  const buildProfileData = () => ({
    displayName: displayName.trim(),
    country,
    language,
    marketingConsent,
    acceptTerms,
    termsAcceptedAt: new Date().toISOString(),
  });

  const validateProfile = () => {
    if (!displayName.trim()) {
      setErrorMsg('Choisis un nom public visible dans le studio.');
      return false;
    }
    if (!acceptTerms) {
      setErrorMsg('Tu dois accepter les CGU et la Politique de confidentialité.');
      return false;
    }
    return true;
  };

  // Soumission email/password
  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (mode === 'reset') {
      await resetPasswordFlow({ email });
      return;
    }

    if (mode === 'signup') {
      if (!validateProfile()) return;
      if (password !== confirmPassword) {
        setErrorMsg('Les mots de passe ne correspondent pas.');
        return;
      }
      const ok = await registerWithEmail({
        email,
        password,
        profileData: buildProfileData(),
        onSuccess: () => handleAuthSuccess(true, 'email'),
      });
      return ok;
    }

    // Login
    await loginWithEmail({ 
      email, 
      password, 
      onSuccess: () => handleAuthSuccess(false, 'email') 
    });
  };

  // Auth Google
  const handleGoogleClick = async () => {
    clearMessages();
    
    if (mode === 'signup') {
      // Pour l'inscription : d'abord le popup Google pour choisir le compte
      // Puis on demandera les infos profil à l'étape 2
      setStep(2);
      setPendingGoogleUser(true);
      return;
    }

    // Pour la connexion, on lance directement Google
    await googleAuth({
      isRegister: false,
      onSuccess: () => handleAuthSuccess(false, 'google'),
    });
  };

  // Compléter le profil après avoir choisi "S'inscrire avec Google"
  const handleCompleteGoogleSignup = async () => {
    clearMessages();
    if (!validateProfile()) return;
    
    // Maintenant on lance le popup Google avec les données du profil
    const ok = await googleAuth({
      isRegister: true,
      profileData: buildProfileData(),
      onSuccess: () => handleAuthSuccess(true, 'google'),
    });
    
    if (!ok) {
      // Si erreur (ex: popup fermé), on reste sur step 2
      return;
    }
  };

  const switchMode = (newMode) => {
    clearMessages();
    setMode(newMode);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Hero Section (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500 rounded-full filter blur-3xl" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl" />
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-16 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10">
              <img src={appIcon} alt="Muzo Studio" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-white tracking-wide">
              Muzo<span className="text-indigo-400">Studio</span>
            </span>
          </div>

          {/* Main content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                Transforme tes vidéos<br />
                <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  avec l'IA
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-md">
                Double la résolution de tes vidéos Sora, supprime les watermarks et libère ton potentiel créatif.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <FeatureCard key={f.title} {...f} />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <span>© 2026 Muzo Studio</span>
            <a href="/legal" className="hover:text-white transition-colors">CGU</a>
            <a href="/privacy" className="hover:text-white transition-colors">Confidentialité</a>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-slate-50 dark:bg-slate-900">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="relative w-10 h-10">
              <img src={appIcon} alt="Muzo Studio" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-slate-900 dark:text-white tracking-wide">
              Muzo<span className="text-indigo-500">Studio</span>
            </span>
          </div>

          {/* Form header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {step === 2 ? 'Finalise ton inscription' :
               mode === 'reset' ? 'Réinitialiser le mot de passe' : 
               mode === 'signup' ? 'Créer un compte' : 
               'Content de te revoir !'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              {step === 2 ? 'Plus que quelques infos et c\'est parti !' :
               mode === 'reset' ? 'Entre ton email pour recevoir un lien' :
               mode === 'signup' ? 'Rejoins des milliers de créateurs' :
               'Connecte-toi pour accéder à ton studio'}
            </p>
          </div>

          {/* Alerts */}
          {(errorMsg || infoMsg) && (
            <div className="mb-6">
              <Alert 
                type={errorMsg ? 'error' : 'success'} 
                message={errorMsg || infoMsg} 
                onDismiss={clearMessages}
              />
              {googleOnly && !errorMsg && (
                <button
                  onClick={() => googleAuth({ isRegister: false, onSuccess: () => handleAuthSuccess(false, 'google') })}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-white font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <GoogleIcon />
                  <span>Continuer avec Google</span>
                </button>
              )}
            </div>
          )}

          {/* Google button (primary for login/signup step 1) */}
          {mode !== 'reset' && step === 1 && (
            <>
              <button
                onClick={handleGoogleClick}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-white font-medium hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-400 dark:hover:border-slate-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              >
                <GoogleIcon />
                <span>{mode === 'signup' ? "S'inscrire avec Google" : 'Continuer avec Google'}</span>
              </button>

              <Divider text="ou par email" />
            </>
          )}

          {/* Step 2: Complete profile for Google signup */}
          {step === 2 && pendingGoogleUser && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-300 text-sm flex items-center gap-2">
                <GoogleIcon />
                <span>Inscription avec Google</span>
              </div>

              <InputField
                id="displayName"
                label="Nom public"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ex: Paul D."
                required
                autoComplete="name"
              />
              
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  id="country"
                  label="Pays"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  options={COUNTRY_OPTIONS}
                  required
                />
                <SelectField
                  id="language"
                  label="Langue"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  options={LANGUAGE_OPTIONS}
                  required
                />
              </div>

              <div className="space-y-3 p-4 rounded-xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    required
                    className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    J'accepte les{' '}
                    <a href="/legal/cgu" className="text-indigo-600 dark:text-indigo-400 hover:underline" target="_blank" rel="noreferrer">CGU</a>
                    {' '}et la{' '}
                    <a href="/legal/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline" target="_blank" rel="noreferrer">Politique de confidentialité</a>
                  </span>
                </label>
                
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Je souhaite recevoir les nouveautés et offres par email
                  </span>
                </label>
              </div>

              <button
                onClick={handleCompleteGoogleSignup}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-white font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-400 dark:hover:border-slate-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <FontAwesomeIcon icon="spinner" spin />
                    Connexion...
                  </span>
                ) : (
                  <>
                    <GoogleIcon />
                    <span>Continuer avec Google</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep(1); setPendingGoogleUser(null); }}
                className="w-full text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                <FontAwesomeIcon icon="arrow-left" className="mr-2" />
                Retour
              </button>
            </div>
          )}

          {/* Form (step 1 only) */}
          {step === 1 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Profile fields for email signup */}
            {mode === 'signup' && (
              <>
                <InputField
                  id="displayName"
                  label="Nom public"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Ex: Paul D."
                  required
                  autoComplete="name"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <SelectField
                    id="country"
                    label="Pays"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    options={COUNTRY_OPTIONS}
                    required
                  />
                  <SelectField
                    id="language"
                    label="Langue"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    options={LANGUAGE_OPTIONS}
                    required
                  />
                </div>
              </>
            )}

            {/* Email */}
            <InputField
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              required
              autoComplete="email"
            />

            {/* Password (not for reset mode) */}
            {mode !== 'reset' && (
              <PasswordField
                id="password"
                label="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                showPassword={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            )}

            {/* Confirm password (signup only) */}
            {mode === 'signup' && (
              <PasswordField
                id="confirmPassword"
                label="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                showPassword={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
                autoComplete="new-password"
              />
            )}

            {/* Terms & Marketing (signup only) */}
            {mode === 'signup' && (
              <div className="space-y-3 p-4 rounded-xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    required
                    className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    J'accepte les{' '}
                    <a href="/legal/cgu" className="text-indigo-600 dark:text-indigo-400 hover:underline" target="_blank" rel="noreferrer">CGU</a>
                    {' '}et la{' '}
                    <a href="/legal/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline" target="_blank" rel="noreferrer">Politique de confidentialité</a>
                  </span>
                </label>
                
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Je souhaite recevoir les nouveautés et offres par email
                  </span>
                </label>
              </div>
            )}

            {/* Forgot password link (login only) */}
            {mode === 'login' && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => switchMode('reset')}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/25"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon="spinner" spin />
                  Chargement...
                </span>
              ) : (
                mode === 'reset' ? 'Envoyer le lien' :
                mode === 'signup' ? "Créer mon compte" :
                'Se connecter'
              )}
            </button>
          </form>
          )}

          {/* Mode switch (step 1 only) */}
          {step === 1 && (
          <div className="mt-6 text-center">
            {mode === 'reset' ? (
              <button
                onClick={() => switchMode('login')}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                <FontAwesomeIcon icon="arrow-left" className="mr-2" />
                Retour à la connexion
              </button>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {mode === 'signup' ? 'Déjà un compte ?' : 'Pas encore de compte ?'}
                <button
                  onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}
                  className="ml-1 text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                >
                  {mode === 'signup' ? 'Se connecter' : "S'inscrire gratuitement"}
                </button>
              </p>
            )}
          </div>
          )}

          {/* Mobile features (shown below form on small screens) */}
          {step === 1 && (
          <div className="lg:hidden mt-10 pt-8 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 text-center">
              Pourquoi Muzo Studio ?
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.slice(0, 2).map((f) => (
                <div key={f.title} className="flex items-center gap-2 p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <FontAwesomeIcon icon={f.icon} className="text-indigo-500" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{f.title}</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
